import { Server } from 'socket.io';
import { AuthSocket } from '../socket.bootstrap';
import { SocketEvents, SendMessagePayload } from '@ichat/shared-types';
import { prisma } from '../../shared/prisma';
import { getChannelMessages, getConversationMessages } from '../../modules/messages/messages.service';

export function messageHandler(io: Server, socket: AuthSocket) {

  socket.on('MESSAGE_FETCH', async (payload: { channelId?: string; conversationId?: string; cursor?: string; limit?: number }, callback: (response: any) => void) => {
    try {
      const limit = payload.limit || 50;
      let messages: any[] = [];
      if (payload.channelId) {
        messages = await getChannelMessages(payload.channelId, payload.cursor, limit);
      } else if (payload.conversationId) {
        messages = await getConversationMessages(payload.conversationId, payload.cursor, limit);
      }
      callback({ success: true, data: messages });
    } catch (err) {
      console.error('message:fetch error', err);
      callback({ success: false, error: 'Failed to fetch messages' });
    }
  });

  socket.on(SocketEvents.MESSAGE_SEND, async (payload: SendMessagePayload) => {
    try {
      // ✅ Validate that the sender is actually a member of the target room
      if (payload.conversationId) {
        const membership = await prisma.conversationMember.findUnique({
          where: { conversationId_userId: { conversationId: payload.conversationId, userId: socket.userId } },
        });
        if (!membership) {
          console.warn(`⛔ Unauthorized MESSAGE_SEND: user ${socket.userId} -> conversation ${payload.conversationId}`);
          socket.emit('error', { message: 'You are not a member of this conversation.' });
          return;
        }
      } else if (payload.channelId) {
        const channel = await prisma.channel.findUnique({
          where: { id: payload.channelId },
          include: {
            members: { where: { userId: socket.userId } },
            team: { include: { members: { where: { userId: socket.userId } } } }
          }
        });

        if (!channel) return;
        const isMember = channel.members.length > 0;
        const isTeamMember = channel.team.members.length > 0;

        if (!isMember && (channel.isPrivate || !isTeamMember)) {
          console.warn(`⛔ Unauthorized MESSAGE_SEND: user ${socket.userId} -> channel ${payload.channelId}`);
          socket.emit('error', { message: 'You are not a member of this channel.' });
          return;
        }
      } else {
        // No target specified — reject
        socket.emit('error', { message: 'Invalid message target.' });
        return;
      }

      const metadataObj = (payload.metadata as any) || {};
      let createAttachments: any[] = [];
      if (metadataObj.attachments && Array.isArray(metadataObj.attachments)) {
        createAttachments = metadataObj.attachments.map((att: any) => ({
          uploadedBy: socket.userId,
          filename: att.name || att.filename,
          mimeType: att.mimeType,
          fileSize: att.size || att.fileSize,
          storageKey: att.storageKey || 'upload',
          storageUrl: att.storageUrl || att.data // fallback for old data
        }));
        delete metadataObj.attachments; // Remove from JSON payload
      }

      const message = await prisma.message.create({
        data: {
          channelId: payload.channelId,
          conversationId: payload.conversationId,
          senderId: socket.userId,
          content: payload.content,
          contentType: (payload.contentType as any) ?? 'text',
          parentId: payload.parentId,
          metadata: metadataObj,
          ...(createAttachments.length > 0 ? { attachments: { create: createAttachments } } : {})
        },
        select: {
          id: true, channelId: true, conversationId: true, senderId: true, parentId: true,
          content: true, contentType: true, metadata: true, isEdited: true, isDeleted: true, createdAt: true,
          sender: { select: { id: true, displayName: true, avatarUrl: true } },
          reactions: true, attachments: true,
          parent: {
            select: {
              id: true,
              content: true,
              contentType: true,
              isDeleted: true,
              sender: { select: { id: true, displayName: true } },
              attachments: { select: { id: true, filename: true, mimeType: true } }
            }
          },
        },
      });

      const room = payload.channelId
        ? `channel:${payload.channelId}`
        : `conversation:${payload.conversationId}`;

      // Ensure sender is in the room (crucial for dynamically created conversations)
      if (!socket.rooms.has(room)) {
        socket.join(room);
      }

      io.to(room).emit(SocketEvents.MESSAGE_NEW, message);
      socket.emit(SocketEvents.MESSAGE_ACK, { messageId: message.id, status: 'delivered' });

      // Notify mentioned users (always saved to DB so it appears in Activity Feed)
      const mentionRegex = /data-user-id="([^"]+)"/g;
      let match;
      const mentionedUserIds = new Set<string>();
      while ((match = mentionRegex.exec(payload.content)) !== null) {
        if (match[1] !== socket.userId) {
          mentionedUserIds.add(match[1]);
        }
      }

      for (const userId of mentionedUserIds) {
        const previewText = payload.content.replace(/<[^>]*>?/gm, '').substring(0, 100);
        await prisma.notification.create({
          data: {
            userId,
            type: 'mention',
            payload: { messageId: message.id, channelId: payload.channelId, conversationId: payload.conversationId, senderId: socket.userId, preview: previewText },
          },
        });
        io.to(`user:${userId}`).emit(SocketEvents.NOTIFICATION_NEW, { type: 'mention', messageId: message.id });
        io.to(`user:${userId}`).emit(SocketEvents.MESSAGE_NEW, message);
      }

      // Notify offline members for channels and conversations (regular messages)
      let targetMembers: { userId: string }[] = [];
      if (payload.channelId) {
        targetMembers = await prisma.channelMember.findMany({
          where: { channelId: payload.channelId, userId: { not: socket.userId } },
          select: { userId: true },
        });
      } else if (payload.conversationId) {
        targetMembers = await prisma.conversationMember.findMany({
          where: { conversationId: payload.conversationId, userId: { not: socket.userId } },
          select: { userId: true },
        });
      }

      for (const { userId } of targetMembers) {
        if (mentionedUserIds.has(userId)) continue; // Already notified via mention

        // Always save to database for the Activity feed
        await prisma.notification.create({
          data: {
            userId,
            type: 'message',
            payload: { messageId: message.id, channelId: payload.channelId, conversationId: payload.conversationId, senderId: socket.userId, preview: payload.content.replace(/<[^>]*>?/gm, '').substring(0, 100) },
          },
        });

        // Emit live event to connected clients
        io.to(`user:${userId}`).emit(SocketEvents.NOTIFICATION_NEW, { type: 'message', messageId: message.id });
        io.to(`user:${userId}`).emit(SocketEvents.MESSAGE_NEW, message);
      }
    } catch (err) {
      socket.emit('error', { message: 'Failed to send message' });
      console.error('message:send error', err);
    }
  });

  socket.on(SocketEvents.MESSAGE_EDIT, async ({ messageId, content }: { messageId: string; content: string }) => {
    try {
      const msg = await prisma.message.findUnique({ where: { id: messageId } });
      if (!msg || msg.senderId !== socket.userId) return;
      const updated = await prisma.message.update({
        where: { id: messageId },
        data: { content, isEdited: true },
        select: { id: true, content: true, isEdited: true, channelId: true, conversationId: true },
      });
      const room = updated.channelId ? `channel:${updated.channelId}` : `conversation:${updated.conversationId}`;
      io.to(room).emit(SocketEvents.MESSAGE_EDITED, updated);
    } catch (err) {
      console.error('message:edit error', err);
    }
  });

  socket.on(SocketEvents.MESSAGE_DELETE, async ({ messageId }: { messageId: string }) => {
    try {
      const msg = await prisma.message.findUnique({ where: { id: messageId } });
      if (!msg || msg.senderId !== socket.userId) return;
      await prisma.message.update({ where: { id: messageId }, data: { isDeleted: true } });
      const room = msg.channelId ? `channel:${msg.channelId}` : `conversation:${msg.conversationId}`;
      io.to(room).emit(SocketEvents.MESSAGE_DELETED, { messageId });
    } catch (err) {
      console.error('message:delete error', err);
    }
  });

  socket.on('message:undo_delete', async ({ messageId }: { messageId: string }) => {
    try {
      const msg = await prisma.message.findUnique({ where: { id: messageId } });
      if (!msg || msg.senderId !== socket.userId) return;
      await prisma.message.update({ where: { id: messageId }, data: { isDeleted: false } });
      const room = msg.channelId ? `channel:${msg.channelId}` : `conversation:${msg.conversationId}`;
      io.to(room).emit('message:restored', { messageId, content: msg.content });
    } catch (err) {
      console.error('message:undo_delete error', err);
    }
  });

  socket.on(SocketEvents.REACTION_ADD, async ({ messageId, emoji }: { messageId: string; emoji: string }) => {
    try {
      await prisma.reaction.upsert({
        where: { messageId_userId_emoji: { messageId, userId: socket.userId, emoji } },
        update: {},
        create: { messageId, userId: socket.userId, emoji },
      });
      const msg = await prisma.message.findUnique({ where: { id: messageId }, select: { channelId: true, conversationId: true, senderId: true, content: true } });
      const room = msg?.channelId ? `channel:${msg.channelId}` : `conversation:${msg?.conversationId}`;
      io.to(room!).emit(SocketEvents.REACTION_UPDATED, { messageId, userId: socket.userId, emoji, action: 'add' });

      // Notify the message author if someone else reacted
      if (msg && msg.senderId !== socket.userId) {
        const notifPayload: any = { messageId };
        if (msg.channelId) notifPayload.channelId = msg.channelId;
        if (msg.conversationId) notifPayload.conversationId = msg.conversationId;

        const sender = await prisma.user.findUnique({ where: { id: socket.userId }, select: { displayName: true } });

        const newNotif = await prisma.notification.create({
          data: {
            userId: msg.senderId,
            type: 'reaction',
            payload: {
              ...notifPayload,
              preview: `${sender?.displayName || 'Someone'} reacted ${emoji} to your message: "${msg.content.substring(0, 30)}${msg.content.length > 30 ? '...' : ''}"`
            }
          }
        });
        io.to(`user:${msg.senderId}`).emit(SocketEvents.NOTIFICATION_NEW, newNotif);
      }
    } catch (err) {
      console.error('reaction:add error', err);
    }
  });

  socket.on(SocketEvents.REACTION_REMOVE, async ({ messageId, emoji }: { messageId: string; emoji: string }) => {
    try {
      await prisma.reaction.deleteMany({
        where: { messageId, userId: socket.userId, emoji }
      });
      const msg = await prisma.message.findUnique({ where: { id: messageId }, select: { channelId: true, conversationId: true } });
      const room = msg?.channelId ? `channel:${msg.channelId}` : `conversation:${msg?.conversationId}`;
      io.to(room!).emit(SocketEvents.REACTION_UPDATED, { messageId, userId: socket.userId, emoji, action: 'remove' });
    } catch (err) {
      console.error('reaction:remove error', err);
    }
  });

  socket.on('message:read', async (payload: { conversationId?: string; channelId?: string }) => {
    try {
      const readAt = new Date();
      if (payload.conversationId) {
        await prisma.conversationMember.upsert({
          where: { conversationId_userId: { conversationId: payload.conversationId, userId: socket.userId } },
          update: { lastReadAt: readAt },
          create: { conversationId: payload.conversationId, userId: socket.userId, lastReadAt: readAt },
        });
        const room = `conversation:${payload.conversationId}`;
        io.to(room).emit('message:read', { conversationId: payload.conversationId, userId: socket.userId, readAt });
      } else if (payload.channelId) {
        await prisma.channelMember.upsert({
          where: { channelId_userId: { channelId: payload.channelId, userId: socket.userId } },
          update: { lastReadAt: readAt },
          create: { channelId: payload.channelId, userId: socket.userId, lastReadAt: readAt },
        });
        const room = `channel:${payload.channelId}`;
        io.to(room).emit('message:read', { channelId: payload.channelId, userId: socket.userId, readAt });
      }
    } catch (err) {
      console.error('message:read error', err);
    }
  });
}
